import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { 
  UserGroupIcon,
  AcademicCapIcon,
  EyeIcon,
  BuildingOffice2Icon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { getCompetencyMatrix, getRoles } from "../../../../api/hse/competency";
import { PlusIcon } from "@heroicons/react/24/outline";
import { fetchDepartments } from "../../../../api/department";
import { toast } from "react-hot-toast";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Matrix - Competency matrix overview (management)
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyMatrix = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    departments: [],
  });
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    if (canManage) {
      fetchDepartmentsList();
      fetchMatrixData();
    }
  }, [canManage, filters]);

  const fetchDepartmentsList = async () => {
    try {
      const response = await fetchDepartments({});
      // Ensure we always have an array
      let departmentsList = [];
      if (Array.isArray(response)) {
        departmentsList = response;
      } else if (response?.departments && Array.isArray(response.departments)) {
        departmentsList = response.departments;
      } else if (response?.data && Array.isArray(response.data)) {
        departmentsList = response.data;
      }
      setDepartments(departmentsList);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setDepartments([]); // Set to empty array on error
    }
  };

  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        search: filters.query,
        department: filters.departments.length > 0 ? filters.departments[0] : undefined,
      };
      
      const response = await getCompetencyMatrix(apiFilters);
      setRoles(response.roles || []);
    } catch (err) {
      console.error("Error fetching matrix data:", err);
      setError(true);
      toast.error(t("Kunne ikke laste kompetansematrise"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      departments: newFilters.departments || [],
    });
  };

  // Department options for ModuleFilter
  const departmentOptions = Array.isArray(departments) ? departments.map(dept => ({
    value: dept.id,
    label: dept.name,
  })) : [];

  // Custom dropdown filters
  const customDropdownFilters = [
    {
      key: "departments",
      label: t("Avdeling"),
      icon: BuildingOffice2Icon,
      options: departmentOptions.map(dept => ({
        value: dept.value,
        label: dept.label,
        class: "bg-blue-100 text-blue-800",
      })),
    },
  ];

  if (!canManage) {
    navigate("/dashboard/hse-ks/competency");
    return null;
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste kompetansematrise")]} />;
  }

  const headerActions = [
    {
      label: t("Ny rolle"),
      icon: PlusIcon,
      path: "/dashboard/hse-ks/competency/roles/new",
      hidden: false,
      menu: false,
    },
  ];

  return (
    <HsePageChrome
      title={t("Kompetansematrise")}
      subtitle={t("Oversikt over kompetanse per rolle")}
      actions={headerActions}
      backButton={{
        label: t("Tilbake til kompetanse"),
        action: () => navigate("/dashboard/hse-ks/competency"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <CompetencySubnav />
          {/* Quick Start Guide - Show if no roles */}
          {roles.length === 0 && !loading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                {t("Kom i gang med kompetansematrise")}
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                {t("For å bruke kompetansematrisen må du først opprette roller. Roller definerer hvilke kompetanser som kreves for ulike stillinger i organisasjonen.")}
              </p>
              <button
                onClick={() => navigate("/dashboard/hse-ks/competency/roles/new")}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                {t("Opprett første rolle")}
              </button>
            </div>
          )}
          
          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i roller...")}
            customDropdownFilters={customDropdownFilters}
          />

          {/* Roles List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Roller")} ({roles.length})
              </h3>
              
              {roles.length === 0 ? (
                <div className="text-center py-12">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">{t("Ingen roller")}</h3>
                  <p className="mt-1 text-sm text-gray-500">{t("Kompetansematrisen krever at du oppretter roller først.")}</p>
                  <div className="mt-6">
                    <button
                      onClick={() => navigate("/dashboard/hse-ks/competency/roles/new")}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      {t("Opprett første rolle")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Rolle")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Avdeling")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Påkrevde kompetanser")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Ansatte")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {roles.map((role) => (
                        <tr key={role.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <UserGroupIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div className="font-medium text-gray-900">{role.name}</div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {role.department_name || role.department ? (
                              <span className="flex items-center">
                                <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                                {role.department_name || role.department}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <AcademicCapIcon className="h-4 w-4 mr-1 text-blue-600" />
                              {role.required_competencies_count || 0}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {role.employees_count || 0}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/dashboard/hse-ks/competency/matrix/${role.id}`}
                              className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              {t("Vis matrise")}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AcademicCapIcon className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t("Om kompetansematrisen")}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    {t("Kompetansematrisen viser hvilke kompetanser som er påkrevd for hver rolle i organisasjonen. Klikk på en rolle for å se detaljert oversikt.")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

